/**
 * Lightweight API client.
 *
 * Authenticated routes use Clerk's session token; unauthenticated routes
 * (the /api/book/* public surface) just hit the same paths without a header.
 *
 * In dev, Vite proxies /api → wrangler dev. In prod the Worker serves both.
 */

const BASE = import.meta.env.VITE_API_BASE_URL || '';

export type ApiError = { error: string; reason?: string; [k: string]: unknown };

export async function api<T>(
  path: string,
  opts: { method?: string; body?: unknown; token?: string | null } = {},
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;

  const res = await fetch(`${BASE}/api${path}`, {
    method: opts.method ?? (opts.body ? 'POST' : 'GET'),
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 204) return undefined as T;

  let body: unknown;
  const text = await res.text();
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = { error: 'invalid_json', raw: text };
  }
  if (!res.ok) {
    // Prefer the human-readable `reason` (the upstream detail, e.g. the Clerk
    // message behind `invitation_failed`) over the opaque machine `error` code
    // for the thrown message. Machine code is still on err.body.error.
    const apiBody = body as ApiError | undefined;
    const err = new Error(apiBody?.reason || apiBody?.error || `http_${res.status}`) as Error & {
      status: number;
      body: unknown;
    };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body as T;
}
